import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useManageHubModalStore } from './ManageHubStore';

import { AddHubToObjectRequest } from 'api/traps/Hubs/types';
import { MLButton } from 'components/Button/Button';
import { MLInput } from 'components/Input/Input';
import { MLSelect } from 'components/Select/Select';
import useGetHubsData from 'hooks/Traps/Hubs/useGetAddHubsData';
import useAddHubToObj from 'hooks/Traps/Hubs/useAddHubToObj';
import MLTextArea from 'components/Input/TextArea';

interface ManageHubModalProps {
  refetchHubs: () => void;
}

const ManageHubModal: React.FC<ManageHubModalProps> = ({ refetchHubs }) => {
  const { isOpen, onManageHubModalClose: onHubModalClose, slug } = useManageHubModalStore();
  const { hubOptions } = useGetHubsData(slug);

  const { t } = useTranslation();

  const { addHubToObject } = useAddHubToObj();

  const { handleSubmit, control, reset } = useForm<AddHubToObjectRequest>({
    defaultValues: {
      hub: '',
      sequence_number: '',
      installment_comment: '',
    },
  });

  const onReset = () => {
    reset();
    onHubModalClose();
  };

  const addHubsToObject = (data: AddHubToObjectRequest) => {
    addHubToObject(
      { addData: data, slug },
      {
        onSuccess: () => {
          enqueueSnackbar('Hub added successfully', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
          refetchHubs();
        },
        onError: () => {
          enqueueSnackbar('Trap did`t add', {
            variant: 'error',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
        },
      },
    );
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={onHubModalClose} size="md">
        <ModalHeader toggle={onReset} className=" p-3 bg-soft-info">
          {`${t('TrapModals.manageModal.installHub')}`}
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="row">
              <div className="mb-3 col-md-8">
                <Controller
                  control={control}
                  name="hub"
                  render={({ field, formState: { errors } }) => (
                    <MLSelect
                      {...field}
                      label={t('TrapModals.manageModal.hub')}
                      error={errors?.hub?.message}
                      value={field.value}
                      required
                      onChange={selectedOption => {
                        field.onChange(selectedOption);
                      }}
                      {...(!hubOptions?.hubs || hubOptions?.hubs.length === 0
                        ? {
                            disabled: true,
                            options: [{ value: '', label: 'No hubs on site' }],
                          }
                        : {
                            options: [{ value: '', label: `${t('TrapModals.manageModal.selectHub')}` }].concat(
                              hubOptions?.hubs?.map(hub => ({
                                value: hub.id.toString(),
                                label: hub.serial_number,
                              })),
                            ),
                          })}
                    />
                  )}
                />
              </div>
              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="sequence_number"
                  rules={{ required: 'Required field', min: { value: 1, message: 'Sequence number must be positive' } }}
                  render={({ field, formState: { errors } }) => (
                    <MLInput
                      {...field}
                      label={t('TrapModals.manageModal.sequenceNumber')}
                      error={errors?.sequence_number?.message}
                      required
                      onChange={e => {
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-12">
                <Controller
                  name="installment_picture"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <MLInput
                      label={t('TrapModals.manageModal.installmentPicture')}
                      type="file"
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        field.onChange(file);
                      }}
                      error={errors?.installment_picture?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-12">
                <Controller
                  name="installment_comment"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <MLTextArea
                      {...field}
                      inputClassName="form-control"
                      label={t('TrapModals.manageModal.installmentComment')}
                      error={errors?.installment_comment?.message}
                      rows={5}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="w-100 d-flex justify-content-end">
            <MLButton
              onClick={handleSubmit(addHubsToObject)}
              isGhost
              color="success"
              innerText={t('TrapModals.confirm')}
            />
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageHubModal;
